import React, { useEffect } from 'react';
import { useStore } from '../../../../../stores/root';

// assets
import { faCheck, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../services/general/error';
import ValidationService from '../../../../../services/validation';
import GeneralService from '../../../../../services/general';

// props

// components
import { DefaultButton, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack } from '@fluentui/react';
import VesselService from '../../../../../services/vessels/vessel';
import SelectVessel from '../../../../uiframeworks/forms/vessels';
import { IVesselCrewResourceProps } from '../../../../../props/vessels/crew';
import VesselCrewService from '../../../../../services/vessels/crew';
import SelectEmployee from '../../../../uiframeworks/forms/selectEmployee';
import SelectVesselCrewRole from '../../../../uiframeworks/forms/data/vessels/crewRole';

interface VesselCrewFormProps {
    vesselId: string;
    crewId?: string;
    onDismissed(refresh?: boolean, status?: string): void;
}

const VesselCrewForm: React.FC<VesselCrewFormProps> = (props: VesselCrewFormProps) => {
    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [data, setData] = React.useState<Partial<IVesselCrewResourceProps>>({
        id: GeneralService.guid()
    });
    const [error, setError] = React.useState<any>({});
    const mode = props.crewId ? 'update' : 'create';

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        try {
            let _data = data;

            if (props.crewId) {
                const crew = await VesselCrewService.read(props.vesselId, props.crewId);
                _data = crew;
            } else if (props.vesselId) {
                const vessel = await VesselService.read(props.vesselId);
                _data.vessel = vessel;
            }

            setData({ ..._data });
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'get_vessel_crew_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: `Failed to get vessel crew. Error: ${ErrorService.getMessage(e)}`
            });
            props.onDismissed();
        }
    }

    const isSubmitButtonDisabled = (): boolean => {
        if (!data.vessel || !data.user || !data.role) {
            return true;
        }

        return false;
    }

    const _onSubmit = async () => {
        try {
            setSubmitting(true);
            const { user, role, vessel } = data;

            // create form data
            const fd = new FormData();
            fd.append('userId', user?.id || "");
            fd.append('roleId', role?.id || "");

            let crew = undefined;
            if (props.crewId && vessel) {
                crew = await VesselCrewService.update(vessel.id, props.crewId, fd);
            } else if (vessel) {
                crew = await VesselCrewService.create(vessel.id, fd);
            }

            banner.add({
                key: 'add_crew_success',
                variant: 'success',
                icon: faCheck,
                text: `Vessel crew "${user?.name}" has been ${mode}d.`
            });
            props.onDismissed(true);
        } catch (e) {
            setSubmitting(false);
        }
    }

    return <Panel headerText={mode === 'create' ? 'Add Vessel Crew' : 'Update Vessel Crew'}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed(false)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                {
                    !submitting ? (
                        <>
                            <PrimaryButton text={mode === 'create' ? 'Create' : 'Update'} disabled={isSubmitButtonDisabled()} onClick={_onSubmit} />
                            <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(false) }} />
                        </>
                    ) : null
                }
                {submitting ? <Spinner size={SpinnerSize.medium} labelPosition={"right"} label={mode === 'create' ? "Adding crew ..." : "Updating crew ..."} /> : null}
            </Stack>;
        }}>
        <Stack tokens={{ childrenGap: 20 }}>
            {!loaded ? <Stack horizontalAlign={"baseline"}><Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Preparing form ..."} /></Stack> : null}
            {
                loaded ? <>
                    <SelectVessel label={"Vessel"}
                        selected={data.vessel}
                        required
                        onChange={(selected) => {
                            let _data = data;
                            _data.vessel = selected;

                            const validation = ValidationService.combination(selected, ['required'], {});
                            error.vessel = validation.message;

                            setData({ ...data });
                            setError({ ...error });
                        }} />
                    <Stack className={'divider'} />
                    <SelectEmployee label={"User"}
                        selected={data.user}
                        required
                        errorMessage={error.user}
                        onChange={(selected) => {
                            let _data = data;
                            _data.user = selected;

                            const validation = ValidationService.combination(selected, ['required'], {});
                            error.user = validation.message;

                            setData({ ...data });
                            setError({ ...error });
                        }} />
                    <SelectVesselCrewRole label={"Role"}
                        selected={data.role}
                        required
                        onChange={(selected) => {
                            let _data = data;
                            _data.role = selected;

                            const validation = ValidationService.combination(selected, ['required'], {});
                            error.role = validation.message;

                            setData({ ...data });
                            setError({ ...error });
                        }} />
                </> : null
            }
        </Stack>
    </Panel>
};

export default VesselCrewForm;
